import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
} from "@mui/material";
import { ManageAccounts, BlockOutlined, DeleteOutline, ScheduleOutlined, SearchOutlined, ArrowBack } from "@mui/icons-material";
import { getAllAnalyst } from "../../../../redux/actions/adminAction";
import { useAdminCredentials } from "../../hooks/useAdminCredentials";
import { ORANGE, humanizeError } from "../../components/ui";
import LoginHistoryList from "../../components/LoginHistoryList";
import { isMultiDevice } from "../../deviceSignals";

const MULTI_DEVICE_HINT =
  "This passkey can sync across the analyst's devices, so sign-ins may come from more than one physical device.";

const STATUS_COLOR = { active: "success", disabled: "warning", deleted: "default" };

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch (e) {
    return iso;
  }
}

/** Admin: look up an analyst and disable/delete/expire their credentials. */
export default function AdminCredentialsPage() {
  const location = useLocation();
  const nav = useHistory();
  const dispatch = useDispatch();
  const token = useSelector((s) => s.auth.token);
  const allAnalyst = useSelector((s) => s.admin.allAnalyst) || [];

  const [query, setQuery] = useState("");
  const [analystId, setAnalystId] = useState(null);
  const [analystSearch, setAnalystSearch] = useState("");
  const { credentials, loginHistory, disable, remove, setExpiry } = useAdminCredentials(analystId);

  // Load the analyst directory once (same source the management page uses).
  useEffect(() => {
    if (token && allAnalyst.length === 0) dispatch(getAllAnalyst({ token }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Deep-link support: /admin/webauthn/credentials?analyst=4821 auto-loads that
  // analyst (used by the "Manage devices" shortcut on the approvals page).
  useEffect(() => {
    const fromUrl = new URLSearchParams(location.search).get("analyst");
    if (fromUrl) {
      setQuery(fromUrl);
      setAnalystId(fromUrl);
    }
  }, [location.search]);

  const [expiryTarget, setExpiryTarget] = useState(null);
  const [days, setDays] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const creds = (credentials.data || []).filter((c) => c.status !== "deleted");

  // Filter the directory by name, username, or id.
  const filteredAnalysts = useMemo(() => {
    const q = analystSearch.trim().toLowerCase();
    if (!q) return allAnalyst;
    return allAnalyst.filter((a) => {
      const name = (a.name || "").toLowerCase();
      const uname = (a.username || "").toLowerCase();
      return name.includes(q) || uname.includes(q) || String(a.id || "").includes(q);
    });
  }, [allAnalyst, analystSearch]);

  const runLookup = () => {
    const id = query.trim();
    if (id) setAnalystId(id);
  };

  // Clicking a directory row fills the lookup field and searches immediately.
  const selectAnalyst = (a) => {
    setQuery(String(a.id));
    setAnalystId(String(a.id));
  };

  const submitExpiry = (durationDays) => {
    setExpiry.mutate({ id: expiryTarget.id, durationDays });
    setExpiryTarget(null);
    setDays("");
  };

  return (
    <Box sx={{ maxWidth: 1120, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 0.5 }}>
        <Tooltip title="Back">
          <IconButton
            onClick={() => nav.goBack()}
            size="small"
            sx={{ border: "1px solid var(--border-1)", color: "var(--text-1)", flexShrink: 0 }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <ManageAccounts sx={{ color: ORANGE }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "var(--text-1)" }}>
          Analyst device credentials
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 14, color: "var(--text-2)", mb: 3 }}>
        Search for an analyst or look them up by ID to disable, delete, or set an expiry on their registered devices.
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} gap={3} alignItems="flex-start">
        {/* Directory — search by name, username, or id */}
        <Box sx={{ width: { xs: "100%", md: 300 }, flexShrink: 0, position: { md: "sticky" }, top: 16 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search analyst by name, id, username…"
            value={analystSearch}
            onChange={(e) => setAnalystSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: "var(--text-2)" }} />
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{
              mt: 1.5,
              maxHeight: 460,
              overflowY: "auto",
              border: "1px solid var(--border-1)",
              borderRadius: "12px",
              backgroundColor: "var(--surface-1)",
            }}
          >
            {allAnalyst.length === 0 ? (
              <Stack alignItems="center" py={3}>
                <CircularProgress size={20} sx={{ color: ORANGE }} />
              </Stack>
            ) : filteredAnalysts.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "var(--text-2)", p: 2 }}>No analysts match.</Typography>
            ) : (
              filteredAnalysts.map((a) => {
                const selected = String(a.id) === String(analystId);
                return (
                  <Box
                    key={a.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectAnalyst(a)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && selectAnalyst(a)}
                    sx={{
                      px: 1.75,
                      py: 1.25,
                      cursor: "pointer",
                      borderBottom: "1px solid var(--border-1)",
                      backgroundColor: selected ? "rgba(232,108,0,0.12)" : "transparent",
                      "&:hover": { backgroundColor: selected ? "rgba(232,108,0,0.16)" : "var(--surface-2)" },
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 600, color: selected ? ORANGE : "var(--text-1)" }}
                      noWrap
                    >
                      {a.name || a.username || `Analyst #${a.id}`}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "var(--text-2)" }} noWrap>
                      #{a.id}
                      {a.username ? ` · @${a.username}` : ""}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* Detail — lookup + credentials */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <Stack direction="row" gap={1} sx={{ mb: 3 }}>
            <TextField
              size="small"
              placeholder="Analyst ID (e.g. 4821)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runLookup()}
              sx={{ maxWidth: 260 }}
            />
            <Button
              variant="contained"
              onClick={runLookup}
              disabled={!query.trim()}
              sx={{ textTransform: "none", backgroundColor: ORANGE, "&:hover": { backgroundColor: "rgba(200,93,0,1)" } }}
            >
              Look up
            </Button>
          </Stack>

          {analystId && (
        <>
          {credentials.isLoading && (
            <Stack alignItems="center" py={3}>
              <CircularProgress sx={{ color: ORANGE }} />
            </Stack>
          )}
          {credentials.isError && (
            <Alert severity="error" sx={{ borderRadius: "10px" }}>
              {humanizeError(credentials.error)}
            </Alert>
          )}
          {!credentials.isLoading && !credentials.isError && creds.length === 0 && (
            <Typography sx={{ color: "var(--text-2)", fontSize: 14 }}>
              No credentials for analyst #{analystId}.
            </Typography>
          )}

          <Stack gap={1.5}>
            {creds.map((c) => (
              <Stack
                key={c.id}
                direction={{ xs: "column", md: "row" }}
                alignItems={{ md: "center" }}
                justifyContent="space-between"
                gap={1.5}
                sx={{ p: 2, borderRadius: "12px", backgroundColor: "var(--surface-1)", border: "1px solid var(--border-1)" }}
              >
                <Stack gap={0.25}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>
                      {c.label || "Unnamed browser"}
                    </Typography>
                    <Chip
                      size="small"
                      label={c.status}
                      color={STATUS_COLOR[c.status] || "default"}
                      sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }}
                    />
                    {c.device_type && (
                      <Chip size="small" variant="outlined" label={c.device_type} sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }} />
                    )}
                    {isMultiDevice(c) && (
                      <Tooltip title={MULTI_DEVICE_HINT}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Multi-device"
                          sx={{ height: 20, fontSize: 11, borderColor: ORANGE, color: ORANGE }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: "var(--text-2)" }}>
                    Added {fmt(c.created_at)} · Last used {c.last_used_at ? fmt(c.last_used_at) : "never"}
                    {c.expires_at ? ` · Expires ${fmt(c.expires_at)}` : ""}
                    {c.device_fingerprint ? ` · FP ${String(c.device_fingerprint).slice(0, 8)}` : ""}
                  </Typography>
                </Stack>
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  <Button
                    size="small"
                    startIcon={<ScheduleOutlined sx={{ fontSize: 16 }} />}
                    onClick={() => { setExpiryTarget(c); setDays(""); }}
                    sx={{ textTransform: "none", color: "var(--text-2)" }}
                  >
                    Expiry
                  </Button>
                  <Button
                    size="small"
                    startIcon={<BlockOutlined sx={{ fontSize: 16 }} />}
                    disabled={c.status !== "active" || disable.isLoading}
                    onClick={() => disable.mutate(c.id)}
                    sx={{ textTransform: "none", color: "#d98b00" }}
                  >
                    Disable
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                    onClick={() => setConfirmDelete(c)}
                    sx={{ textTransform: "none", color: "#e5484d" }}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>

          {creds.length > 0 && (
            <>
              <Divider sx={{ my: 3, borderColor: "var(--border-1)" }} />
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "var(--text-1)", mb: 1.5 }}>
                Recent login history
              </Typography>
              {loginHistory.isLoading ? (
                <CircularProgress size={20} sx={{ color: ORANGE }} />
              ) : (
                <LoginHistoryList items={loginHistory.data} />
              )}
            </>
          )}
        </>
          )}
        </Box>
      </Stack>

      {/* Set expiry */}
      <Dialog open={!!expiryTarget} onClose={() => setExpiryTarget(null)}>
        <DialogTitle>Set credential expiry</DialogTitle>
        <DialogContent sx={{ minWidth: 340 }}>
          <Typography sx={{ fontSize: 13, color: "var(--text-2)", mb: 2 }}>
            "{expiryTarget?.label}" will stop working after the given number of days. Clear it to remove any expiry.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="number"
            placeholder="Days (e.g. 30)"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => submitExpiry(null)} sx={{ textTransform: "none", color: "var(--text-2)" }}>
            Remove expiry
          </Button>
          <Button
            onClick={() => submitExpiry(parseInt(days, 10))}
            disabled={!days || parseInt(days, 10) < 1}
            sx={{ textTransform: "none", color: ORANGE }}
          >
            Set
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete this credential?</DialogTitle>
        <DialogContent sx={{ minWidth: 340 }}>
          <Typography sx={{ fontSize: 14, color: "var(--text-2)" }}>
            "{confirmDelete?.label}" will be permanently blocked from signing in. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={() => { remove.mutate(confirmDelete.id); setConfirmDelete(null); }}
            sx={{ textTransform: "none", color: "#e5484d" }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
